// Primary 1 English Learning Data
// Based on Primary English Learning P1 Topics.txt

export const p1Data = {
  gradeId: 'p1',
  gradeName: 'Primary 1 (小學一年級)',
  objectives: [
    'Build foundational English vocabulary and grammar',
    'Develop basic reading and writing skills',
    'Learn essential sentence structures',
    'Master fundamental verb forms and tenses',
    'Understand basic punctuation and capitalization'
  ],
  keySkills: [
    'Subject pronouns and verb "to be"',
    'Present continuous tense',
    'Question formation',
    'Basic sentence structure',
    'Common prepositions'
  ],
  topics: [
    {
      id: 'p1-topic-1',
      title: 'Subject Pronouns & Verb to be',
      description: 'Learning personal pronouns and the verb "to be" in present tense',
      purpose: 'To help students understand and use subject pronouns (I, you, he, she, it, we, they) correctly with the verb "to be" (am, is, are) in simple sentences.',
      questions: [
        {
          question: 'Complete the sentence: "I _____ a student."',
          answer: 'am',
          explanation: 'Use "am" with the subject "I" in present tense.'
        },
        {
          question: 'Choose the correct form: "She _____ happy."',
          answer: 'is',
          explanation: 'Use "is" with singular subjects like "he", "she", "it".'
        },
        {
          question: 'Fill in the blank: "They _____ friends."',
          answer: 'are',
          explanation: 'Use "are" with plural subjects like "we", "you", "they".'
        },
        {
          question: 'What is the subject pronoun for a boy?',
          answer: 'he',
          explanation: 'Use "he" to refer to a male person or animal.'
        },
        {
          question: 'Complete: "We _____ students."',
          answer: 'are',
          explanation: 'Use "are" with "we" (first person plural).'
        },
        {
          question: 'Choose the correct form: "It _____ a cat."',
          answer: 'is',
          explanation: 'Use "is" with "it" (singular third person).'
        },
        {
          question: 'Fill in the blank: "You _____ my friend."',
          answer: 'are',
          explanation: 'Use "are" with "you" (second person).'
        },
        {
          question: 'What pronoun do we use for a girl?',
          answer: 'she',
          explanation: 'Use "she" to refer to a female person or animal.'
        },
        {
          question: 'Complete: "He _____ tall."',
          answer: 'is',
          explanation: 'Use "is" with "he" (singular third person).'
        },
        {
          question: 'Choose the correct form: "I _____ hungry."',
          answer: 'am',
          explanation: 'Use "am" with "I" (first person singular).'
        }
      ]
    },
    {
      id: 'p1-topic-2',
      title: 'Conjunction',
      description: 'Learning to connect words, phrases, and sentences using conjunctions',
      purpose: 'To teach students how to use basic conjunctions like "and", "but", "or" to connect ideas and create compound sentences.',
      questions: [
        {
          question: 'Complete: "I like apples _____ oranges."',
          answer: 'and',
          explanation: 'Use "and" to connect similar ideas or add information.'
        },
        {
          question: 'Fill in the blank: "I am tired _____ I want to sleep."',
          answer: 'so',
          explanation: 'Use "so" to show cause and effect relationship.'
        },
        {
          question: 'Choose the correct conjunction: "I like tea _____ I don\'t like coffee."',
          answer: 'but',
          explanation: 'Use "but" to show contrast between two ideas.'
        },
        {
          question: 'Complete: "Do you want tea _____ coffee?"',
          answer: 'or',
          explanation: 'Use "or" to show a choice between two options.'
        },
        {
          question: 'Fill in the blank: "I am hungry _____ I will eat."',
          answer: 'so',
          explanation: 'Use "so" to show the result of being hungry.'
        },
        {
          question: 'Choose the correct conjunction: "I like cats _____ dogs."',
          answer: 'and',
          explanation: 'Use "and" to connect two things you like.'
        },
        {
          question: 'Complete: "It is raining _____ I will stay home."',
          answer: 'so',
          explanation: 'Use "so" to show the result of rain.'
        },
        {
          question: 'Fill in the blank: "I can swim _____ I can\'t dive."',
          answer: 'but',
          explanation: 'Use "but" to show contrast between abilities.'
        },
        {
          question: 'Choose the correct conjunction: "Would you like milk _____ juice?"',
          answer: 'or',
          explanation: 'Use "or" to offer a choice between two drinks.'
        },
        {
          question: 'Complete: "I am happy _____ I got a new toy."',
          answer: 'because',
          explanation: 'Use "because" to explain the reason for being happy.'
        }
      ]
    },
    {
      id: 'p1-topic-3',
      title: 'Present Continuous Tense',
      description: 'Learning to describe actions happening now using present continuous tense',
      purpose: 'To teach students how to form and use the present continuous tense (am/is/are + verb-ing) to describe actions happening at the moment of speaking.',
      questions: [
        {
          question: 'Complete: "I _____ reading a book."',
          answer: 'am',
          explanation: 'Use "am" with "I" in present continuous tense.'
        },
        {
          question: 'Fill in the blank: "She _____ playing in the park."',
          answer: 'is',
          explanation: 'Use "is" with singular subjects in present continuous.'
        },
        {
          question: 'Choose the correct form: "They _____ eating lunch."',
          answer: 'are',
          explanation: 'Use "are" with plural subjects in present continuous.'
        },
        {
          question: 'Complete: "The cat _____ sleeping."',
          answer: 'is',
          explanation: 'Use "is" with "it" (the cat) in present continuous.'
        },
        {
          question: 'Fill in the blank: "We _____ watching TV."',
          answer: 'are',
          explanation: 'Use "are" with "we" in present continuous tense.'
        },
        {
          question: 'Choose the correct form: "He _____ running."',
          answer: 'is',
          explanation: 'Use "is" with "he" in present continuous tense.'
        },
        {
          question: 'Complete: "You _____ studying English."',
          answer: 'are',
          explanation: 'Use "are" with "you" in present continuous.'
        },
        {
          question: 'Fill in the blank: "The birds _____ singing."',
          answer: 'are',
          explanation: 'Use "are" with plural subjects (birds) in present continuous.'
        },
        {
          question: 'Choose the correct form: "It _____ raining."',
          answer: 'is',
          explanation: 'Use "is" with "it" in present continuous tense.'
        },
        {
          question: 'Complete: "I _____ writing a letter."',
          answer: 'am',
          explanation: 'Use "am" with "I" in present continuous tense.'
        }
      ]
    },
    {
      id: 'p1-topic-4',
      title: 'Question Words',
      description: 'Learning to ask questions using question words (what, where, when, who, why, how)',
      purpose: 'To teach students how to form questions using different question words to gather specific information.',
      questions: [
        {
          question: '_____ is your name?',
          answer: 'What',
          explanation: 'Use "What" to ask about things, names, or objects.'
        },
        {
          question: '_____ do you live?',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location or place.'
        },
        {
          question: '_____ is your favorite color?',
          answer: 'What',
          explanation: 'Use "What" to ask about preferences or choices.'
        },
        {
          question: '_____ do you go to school?',
          answer: 'When',
          explanation: 'Use "When" to ask about time or schedule.'
        },
        {
          question: '_____ is your best friend?',
          answer: 'Who',
          explanation: 'Use "Who" to ask about people.'
        },
        {
          question: '_____ do you like ice cream?',
          answer: 'Why',
          explanation: 'Use "Why" to ask for reasons or explanations.'
        },
        {
          question: '_____ old are you?',
          answer: 'How',
          explanation: 'Use "How" to ask about quantity, age, or manner.'
        },
        {
          question: '_____ is your school?',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location.'
        },
        {
          question: '_____ do you eat breakfast?',
          answer: 'When',
          explanation: 'Use "When" to ask about time.'
        },
        {
          question: '_____ is your favorite subject?',
          answer: 'What',
          explanation: 'Use "What" to ask about preferences or choices.'
        }
      ]
    },
    {
      id: 'p1-topic-5',
      title: 'Possessive Adjectives',
      description: 'Learning to show ownership using possessive adjectives (my, your, his, her, its, our, their)',
      purpose: 'To teach students how to use possessive adjectives to show ownership or relationship between people and things.',
      questions: [
        {
          question: 'This is _____ book.',
          answer: 'my',
          explanation: 'Use "my" to show that the book belongs to the speaker.'
        },
        {
          question: 'What is _____ name?',
          answer: 'your',
          explanation: 'Use "your" to ask about the listener\'s name.'
        },
        {
          question: 'This is _____ car.',
          answer: 'his',
          explanation: 'Use "his" to show that the car belongs to a male person.'
        },
        {
          question: 'That is _____ bag.',
          answer: 'her',
          explanation: 'Use "her" to show that the bag belongs to a female person.'
        },
        {
          question: 'The cat is playing with _____ toy.',
          answer: 'its',
          explanation: 'Use "its" to show that the toy belongs to the cat (animal).'
        },
        {
          question: 'This is _____ house.',
          answer: 'our',
          explanation: 'Use "our" to show that the house belongs to the speaker and others.'
        },
        {
          question: 'Those are _____ books.',
          answer: 'their',
          explanation: 'Use "their" to show that the books belong to other people.'
        },
        {
          question: 'Is this _____ pencil?',
          answer: 'your',
          explanation: 'Use "your" to ask if the pencil belongs to the listener.'
        },
        {
          question: 'This is _____ dog.',
          answer: 'my',
          explanation: 'Use "my" to show that the dog belongs to the speaker.'
        },
        {
          question: 'That is _____ school.',
          answer: 'our',
          explanation: 'Use "our" to show that the school belongs to the speaker and others.'
        }
      ]
    },
    {
      id: 'p1-topic-6',
      title: 'Prepositions of Method',
      description: 'Learning to describe how actions are done using prepositions like "by", "with", "in", "on"',
      purpose: 'To teach students how to use prepositions to describe the method or way something is done.',
      questions: [
        {
          question: 'I write _____ a pen.',
          answer: 'with',
          explanation: 'Use "with" to show the tool or instrument used.'
        },
        {
          question: 'She goes to school _____ bus.',
          answer: 'by',
          explanation: 'Use "by" to show the means of transportation.'
        },
        {
          question: 'I eat _____ a spoon.',
          answer: 'with',
          explanation: 'Use "with" to show the utensil used for eating.'
        },
        {
          question: 'He travels _____ train.',
          answer: 'by',
          explanation: 'Use "by" to show the means of transportation.'
        },
        {
          question: 'I draw _____ a pencil.',
          answer: 'with',
          explanation: 'Use "with" to show the tool used for drawing.'
        },
        {
          question: 'She goes to work _____ car.',
          answer: 'by',
          explanation: 'Use "by" to show the means of transportation.'
        },
        {
          question: 'I cut paper _____ scissors.',
          answer: 'with',
          explanation: 'Use "with" to show the tool used for cutting.'
        },
        {
          question: 'He goes to the city _____ plane.',
          answer: 'by',
          explanation: 'Use "by" to show the means of transportation.'
        },
        {
          question: 'I paint _____ a brush.',
          answer: 'with',
          explanation: 'Use "with" to show the tool used for painting.'
        },
        {
          question: 'She goes home _____ taxi.',
          answer: 'by',
          explanation: 'Use "by" to show the means of transportation.'
        }
      ]
    },
    {
      id: 'p1-topic-7',
      title: 'Articles',
      description: 'Learning to use articles (a, an, the) correctly in sentences',
      purpose: 'To teach students how to use definite and indefinite articles to specify or generalize nouns.',
      questions: [
        {
          question: 'I have _____ apple.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds (a, e, i, o, u).'
        },
        {
          question: '_____ sun is bright.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I see _____ cat.',
          answer: 'a',
          explanation: 'Use "a" before words that begin with consonant sounds.'
        },
        {
          question: '_____ moon is beautiful.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I have _____ orange.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ book is on the table.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific book.'
        },
        {
          question: 'I want _____ banana.',
          answer: 'a',
          explanation: 'Use "a" before words that begin with consonant sounds.'
        },
        {
          question: '_____ earth is round.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I have _____ umbrella.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ dog is running.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific dog.'
        }
      ]
    },
    {
      id: 'p1-topic-8',
      title: 'Demonstrative Pronouns & Verb to be',
      description: 'Learning to use this, that, these, those with the verb "to be"',
      purpose: 'To teach students how to use demonstrative pronouns to point to specific people, places, or things, combined with the verb "to be".',
      questions: [
        {
          question: '_____ is my book.',
          answer: 'This',
          explanation: 'Use "This" to refer to a singular thing that is near the speaker.'
        },
        {
          question: '_____ are my friends.',
          answer: 'These',
          explanation: 'Use "These" to refer to plural things that are near the speaker.'
        },
        {
          question: '_____ is a car.',
          answer: 'That',
          explanation: 'Use "That" to refer to a singular thing that is far from the speaker.'
        },
        {
          question: '_____ are flowers.',
          answer: 'Those',
          explanation: 'Use "Those" to refer to plural things that are far from the speaker.'
        },
        {
          question: '_____ is my house.',
          answer: 'This',
          explanation: 'Use "This" to refer to a singular thing that is near the speaker.'
        },
        {
          question: '_____ are my toys.',
          answer: 'These',
          explanation: 'Use "These" to refer to plural things that are near the speaker.'
        },
        {
          question: '_____ is a tree.',
          answer: 'That',
          explanation: 'Use "That" to refer to a singular thing that is far from the speaker.'
        },
        {
          question: '_____ are birds.',
          answer: 'Those',
          explanation: 'Use "Those" to refer to plural things that are far from the speaker.'
        },
        {
          question: '_____ is my school.',
          answer: 'This',
          explanation: 'Use "This" to refer to a singular thing that is near the speaker.'
        },
        {
          question: '_____ are my books.',
          answer: 'These',
          explanation: 'Use "These" to refer to plural things that are near the speaker.'
        }
      ]
    },
    {
      id: 'p1-topic-9',
      title: 'Punctuation',
      description: 'Learning to use basic punctuation marks (period, question mark, exclamation mark)',
      purpose: 'To teach students how to use punctuation marks correctly to end sentences and express different types of statements.',
      questions: [
        {
          question: 'I like ice cream _____',
          answer: '.',
          explanation: 'Use a period (.) to end a statement or declarative sentence.'
        },
        {
          question: 'What is your name _____',
          answer: '?',
          explanation: 'Use a question mark (?) to end a question or interrogative sentence.'
        },
        {
          question: 'Wow, that\'s amazing _____',
          answer: '!',
          explanation: 'Use an exclamation mark (!) to end an exclamation or show strong emotion.'
        },
        {
          question: 'The cat is sleeping _____',
          answer: '.',
          explanation: 'Use a period (.) to end a statement.'
        },
        {
          question: 'Where do you live _____',
          answer: '?',
          explanation: 'Use a question mark (?) to end a question.'
        },
        {
          question: 'Happy birthday _____',
          answer: '!',
          explanation: 'Use an exclamation mark (!) to show excitement or strong emotion.'
        },
        {
          question: 'I am happy _____',
          answer: '.',
          explanation: 'Use a period (.) to end a statement.'
        },
        {
          question: 'How old are you _____',
          answer: '?',
          explanation: 'Use a question mark (?) to end a question.'
        },
        {
          question: 'That\'s incredible _____',
          answer: '!',
          explanation: 'Use an exclamation mark (!) to show surprise or strong emotion.'
        },
        {
          question: 'The sun is bright _____',
          answer: '.',
          explanation: 'Use a period (.) to end a statement.'
        }
      ]
    },
    {
      id: 'p1-topic-10',
      title: 'Plural Nouns',
      description: 'Learning to form and use plural nouns correctly',
      purpose: 'To teach students how to change singular nouns to plural forms and use them correctly in sentences.',
      questions: [
        {
          question: 'I have two _____ (cat).',
          answer: 'cats',
          explanation: 'Add "s" to most nouns to make them plural.'
        },
        {
          question: 'There are three _____ (book) on the table.',
          answer: 'books',
          explanation: 'Add "s" to make "book" plural.'
        },
        {
          question: 'I see many _____ (bird) in the sky.',
          answer: 'birds',
          explanation: 'Add "s" to make "bird" plural.'
        },
        {
          question: 'She has five _____ (pencil).',
          answer: 'pencils',
          explanation: 'Add "s" to make "pencil" plural.'
        },
        {
          question: 'There are two _____ (dog) in the park.',
          answer: 'dogs',
          explanation: 'Add "s" to make "dog" plural.'
        },
        {
          question: 'I have three _____ (apple).',
          answer: 'apples',
          explanation: 'Add "s" to make "apple" plural.'
        },
        {
          question: 'There are four _____ (car) on the road.',
          answer: 'cars',
          explanation: 'Add "s" to make "car" plural.'
        },
        {
          question: 'She has two _____ (sister).',
          answer: 'sisters',
          explanation: 'Add "s" to make "sister" plural.'
        },
        {
          question: 'I see many _____ (tree) in the forest.',
          answer: 'trees',
          explanation: 'Add "s" to make "tree" plural.'
        },
        {
          question: 'There are three _____ (house) on this street.',
          answer: 'houses',
          explanation: 'Add "s" to make "house" plural.'
        }
      ]
    },
    {
      id: 'p1-topic-11',
      title: 'Prepositions of Location',
      description: 'Learning to describe where things are using prepositions like "in", "on", "under", "behind"',
      purpose: 'To teach students how to use prepositions to describe the position or location of people, places, and things.',
      questions: [
        {
          question: 'The book is _____ the table.',
          answer: 'on',
          explanation: 'Use "on" when something is touching the surface of another object.'
        },
        {
          question: 'The cat is _____ the box.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a container or enclosed space.'
        },
        {
          question: 'The ball is _____ the chair.',
          answer: 'under',
          explanation: 'Use "under" when something is below another object.'
        },
        {
          question: 'The car is _____ the house.',
          answer: 'behind',
          explanation: 'Use "behind" when something is at the back of another object.'
        },
        {
          question: 'The bird is _____ the tree.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a natural enclosure like a tree.'
        },
        {
          question: 'The picture is _____ the wall.',
          answer: 'on',
          explanation: 'Use "on" when something is attached to a surface.'
        },
        {
          question: 'The dog is _____ the table.',
          answer: 'under',
          explanation: 'Use "under" when something is below another object.'
        },
        {
          question: 'The boy is _____ the door.',
          answer: 'behind',
          explanation: 'Use "behind" when something is at the back of another object.'
        },
        {
          question: 'The fish is _____ the water.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a liquid or substance.'
        },
        {
          question: 'The cup is _____ the table.',
          answer: 'on',
          explanation: 'Use "on" when something is touching the surface of another object.'
        }
      ]
    },
    {
      id: 'p1-topic-12',
      title: 'Proper Nouns & Common Nouns',
      description: 'Learning to distinguish between proper nouns (names) and common nouns (general things)',
      purpose: 'To teach students the difference between proper nouns (specific names) and common nouns (general categories) and how to use them correctly.',
      questions: [
        {
          question: 'Which is a proper noun: "city" or "Hong Kong"?',
          answer: 'Hong Kong',
          explanation: 'Proper nouns are specific names of people, places, or things and start with capital letters.'
        },
        {
          question: 'Which is a common noun: "Mary" or "girl"?',
          answer: 'girl',
          explanation: 'Common nouns are general names for people, places, or things.'
        },
        {
          question: 'Which is a proper noun: "dog" or "Rex"?',
          answer: 'Rex',
          explanation: 'Proper nouns are specific names and start with capital letters.'
        },
        {
          question: 'Which is a common noun: "London" or "city"?',
          answer: 'city',
          explanation: 'Common nouns are general categories of things.'
        },
        {
          question: 'Which is a proper noun: "book" or "Harry Potter"?',
          answer: 'Harry Potter',
          explanation: 'Proper nouns are specific names and start with capital letters.'
        },
        {
          question: 'Which is a common noun: "John" or "boy"?',
          answer: 'boy',
          explanation: 'Common nouns are general names for people, places, or things.'
        },
        {
          question: 'Which is a proper noun: "school" or "St. Mary\'s"?',
          answer: 'St. Mary\'s',
          explanation: 'Proper nouns are specific names and start with capital letters.'
        },
        {
          question: 'Which is a common noun: "Paris" or "city"?',
          answer: 'city',
          explanation: 'Common nouns are general categories of things.'
        },
        {
          question: 'Which is a proper noun: "car" or "Toyota"?',
          answer: 'Toyota',
          explanation: 'Proper nouns are specific names and start with capital letters.'
        },
        {
          question: 'Which is a common noun: "Emma" or "student"?',
          answer: 'student',
          explanation: 'Common nouns are general names for people, places, or things.'
        }
      ]
    },
    {
      id: 'p1-topic-13',
      title: 'Imperatives',
      description: 'Learning to give commands and instructions using imperative sentences',
      purpose: 'To teach students how to form and use imperative sentences to give commands, instructions, or make requests.',
      questions: [
        {
          question: 'Complete the command: "_____ the door."',
          answer: 'Open',
          explanation: 'Imperatives start with the base form of the verb (no subject needed).'
        },
        {
          question: 'Fill in the blank: "_____ your homework."',
          answer: 'Do',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ quiet, please."',
          answer: 'Be',
          explanation: 'Use "be" as the base form for the verb "to be" in imperatives.'
        },
        {
          question: 'Fill in the blank: "_____ your hands."',
          answer: 'Wash',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ the window."',
          answer: 'Close',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your teeth."',
          answer: 'Brush',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ to me."',
          answer: 'Listen',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your room."',
          answer: 'Clean',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ the ball."',
          answer: 'Throw',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your shoes."',
          answer: 'Wear',
          explanation: 'Use the base form of the verb for imperatives.'
        }
      ]
    },
    {
      id: 'p1-topic-14',
      title: 'Adverbs & Verb to be',
      description: 'Learning to use adverbs with the verb "to be" to describe states or conditions',
      purpose: 'To teach students how to use adverbs to modify the verb "to be" and describe how someone or something is.',
      questions: [
        {
          question: 'Complete: "I am _____ happy."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of happiness.'
        },
        {
          question: 'Fill in the blank: "She is _____ tired."',
          answer: 'quite',
          explanation: 'Use "quite" as an adverb to show a moderate degree.'
        },
        {
          question: 'Complete: "He is _____ tall."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of height.'
        },
        {
          question: 'Fill in the blank: "They are _____ busy."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of being busy.'
        },
        {
          question: 'Complete: "The weather is _____ cold."',
          answer: 'quite',
          explanation: 'Use "quite" as an adverb to show a moderate degree.'
        },
        {
          question: 'Fill in the blank: "I am _____ hungry."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of hunger.'
        },
        {
          question: 'Complete: "She is _____ beautiful."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of beauty.'
        },
        {
          question: 'Fill in the blank: "He is _____ smart."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of intelligence.'
        },
        {
          question: 'Complete: "The food is _____ delicious."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of taste.'
        },
        {
          question: 'Fill in the blank: "I am _____ excited."',
          answer: 'very',
          explanation: 'Use "very" as an adverb to emphasize the degree of excitement.'
        }
      ]
    },
    {
      id: 'p1-topic-15',
      title: 'Quantifiers',
      description: 'Learning to use words that show quantity (some, any, many, much, a few, a little)',
      purpose: 'To teach students how to use quantifiers to express amounts and quantities in English.',
      questions: [
        {
          question: 'I have _____ apples.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with countable nouns.'
        },
        {
          question: 'Do you have _____ water?',
          answer: 'any',
          explanation: 'Use "any" for questions and negative statements with uncountable nouns.'
        },
        {
          question: 'There are _____ books on the shelf.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ money.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        },
        {
          question: 'I have _____ friends.',
          answer: 'a few',
          explanation: 'Use "a few" with countable nouns to show a small quantity.'
        },
        {
          question: 'There is _____ sugar in the bowl.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with uncountable nouns.'
        },
        {
          question: 'Do you have _____ pencils?',
          answer: 'any',
          explanation: 'Use "any" for questions with countable nouns.'
        },
        {
          question: 'I have _____ time.',
          answer: 'a little',
          explanation: 'Use "a little" with uncountable nouns to show a small quantity.'
        },
        {
          question: 'There are _____ students in the class.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ homework.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        }
      ]
    },
    {
      id: 'p1-topic-16',
      title: 'Simple Present Tense',
      description: 'Learning to use simple present tense for habits, facts, and general truths',
      purpose: 'To teach students how to form and use simple present tense to describe habitual actions, facts, and general truths.',
      questions: [
        {
          question: 'I _____ to school every day.',
          answer: 'go',
          explanation: 'Use the base form of the verb for "I" in simple present tense.'
        },
        {
          question: 'She _____ English.',
          answer: 'speaks',
          explanation: 'Add "s" to the verb for third person singular (he, she, it).'
        },
        {
          question: 'They _____ football.',
          answer: 'play',
          explanation: 'Use the base form of the verb for plural subjects (we, you, they).'
        },
        {
          question: 'He _____ in Hong Kong.',
          answer: 'lives',
          explanation: 'Add "s" to the verb for third person singular (he, she, it).'
        },
        {
          question: 'We _____ dinner at 6 PM.',
          answer: 'eat',
          explanation: 'Use the base form of the verb for "we" in simple present tense.'
        },
        {
          question: 'The sun _____ in the east.',
          answer: 'rises',
          explanation: 'Add "s" to the verb for third person singular (it).'
        },
        {
          question: 'I _____ my teeth every morning.',
          answer: 'brush',
          explanation: 'Use the base form of the verb for "I" in simple present tense.'
        },
        {
          question: 'She _____ to music.',
          answer: 'listens',
          explanation: 'Add "s" to the verb for third person singular (she).'
        },
        {
          question: 'They _____ Chinese.',
          answer: 'speak',
          explanation: 'Use the base form of the verb for plural subjects (they).'
        },
        {
          question: 'He _____ basketball.',
          answer: 'plays',
          explanation: 'Add "s" to the verb for third person singular (he).'
        }
      ]
    },
    {
      id: 'p1-topic-17',
      title: 'Modal Verbs',
      description: 'Learning to use basic modal verbs (can, cannot/can\'t, must, should)',
      purpose: 'To teach students how to use modal verbs to express ability, permission, obligation, and advice.',
      questions: [
        {
          question: 'I _____ swim.',
          answer: 'can',
          explanation: 'Use "can" to express ability or possibility.'
        },
        {
          question: 'You _____ not smoke here.',
          answer: 'can',
          explanation: 'Use "can not" or "cannot" to express prohibition.'
        },
        {
          question: 'You _____ study hard.',
          answer: 'should',
          explanation: 'Use "should" to give advice or make recommendations.'
        },
        {
          question: 'She _____ speak English.',
          answer: 'can',
          explanation: 'Use "can" to express ability.'
        },
        {
          question: 'You _____ wear a seatbelt.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation or necessity.'
        },
        {
          question: 'I _____ not drive a car.',
          answer: 'can',
          explanation: 'Use "can not" to express inability.'
        },
        {
          question: 'You _____ eat vegetables.',
          answer: 'should',
          explanation: 'Use "should" to give advice about healthy eating.'
        },
        {
          question: 'He _____ play the piano.',
          answer: 'can',
          explanation: 'Use "can" to express ability.'
        },
        {
          question: 'You _____ be quiet in the library.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation.'
        },
        {
          question: 'We _____ help others.',
          answer: 'should',
          explanation: 'Use "should" to give advice about good behavior.'
        }
      ]
    }
  ]
}; 